import type { ReactNode } from "react";

export interface StatCardProps {
  /** Small caption above the value (e.g. "Total orders"). */
  label: ReactNode;
  /** The stat value. Pass a ReactNode to control prefix/suffix styling
   *  (e.g. `<><span className="…">EGP </span>{amount}</>`). */
  value: ReactNode;
  /** Optional leading icon shown before the label. */
  icon?: ReactNode;
  /** Optional footer rendered on the white area below the gray inset
   *  (e.g. a delta chip). Omitted for the plain insight tiles. */
  footer?: ReactNode;
  className?: string;
}

/**
 * StatCard — the "gray inset" card variant. An outer white card wraps a
 * recessed gray panel (`--surface-alt`) that holds the label + value; an
 * optional footer sits on the white margin below the inset. Used for the
 * dashboard KPI tiles and customer/insight stats. For titled content
 * sections (Contact, Tags, tables) use `SectionCard` — the plain variant.
 */
export function StatCard({
  label,
  value,
  icon,
  footer,
  className = "",
}: StatCardProps) {
  return (
    <div
      className={[
        "rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-[7px]",
        className,
      ].join(" ")}
    >
      <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] px-[14px] py-[13px]">
        <div className="mb-2.5 flex items-center gap-2">
          {icon ? (
            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[7px] border border-[color:var(--border-strong)] bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(16,16,15,0.05)]"
            >
              {icon}
            </span>
          ) : null}
          <span className="min-w-0 truncate text-[12px] font-medium text-[color:var(--text-muted)]">
            {label}
          </span>
        </div>
        <div className="flex items-baseline gap-1 text-[24px] font-bold leading-none tracking-[-0.015em] text-[color:var(--text-primary)]">
          {value}
        </div>
      </div>
      {footer ? <div className="px-2 pb-1.5 pt-[11px]">{footer}</div> : null}
    </div>
  );
}
